/**
 * Gestión → Administración → Generar comprobante
 * (`/gestion/administracion/comprobantes/crear`).
 *
 * Puerto de `Administration::create` + `views/menu/administrador/generar_comprobante.php`
 * (bundle `administrador.generar_comprobante.js`) y de
 * `Administrador::registrar_admin_venta` + `Administrador::crear_cpe`.
 *
 * Qué se porta:
 * - El **formulario** del comprobante: fecha de emisión/vencimiento, tipo
 *   (FACTURA/BOLETA), serie, cliente, dirección, detracción, información
 *   adicional, las **líneas** (producto, descripción PDF, cantidad, precio y
 *   placas) y la **condición de pago** (contado, contado adelantado, crédito o
 *   crédito con cuotas) con su destino de pago.
 * - El **cálculo** de totales: operación total, IGV, total, detracción y total a
 *   pagar (`calular_total_comprobante`).
 * - El **registro**: crea la venta administrativa con su detalle y su comprobante
 *   en la cola de SUNAT, lo envía al servicio de facturación y registra el pago
 *   (caja chica o banco) o el crédito.
 */

import { ArrowLeft, Check, FilePlus2, Plus, Trash2 } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ConfirmDialog, Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { nuevaClaveDeEnvio } from '@/lib/clave-de-envio';
import { ApiError } from '@/lib/http/api-error';

import { AdministracionNav } from '../components/administracion-nav';
import { useCatalogosCrear, useCrearComprobante } from '../comprobantes.api';
import type { LineaCrearComprobante } from '../comprobantes.types';
import { hoyIso, hoyMenosDias, monto } from '../gestion-formato';

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

const IGV = 1.18;

/** Redondea a dos decimales. */
function dinero(valor: number): number {
  return Math.round(valor * 100) / 100;
}

/** Redondea a tres decimales. */
function con3(valor: number): number {
  return Math.round(valor * 1000) / 1000;
}

/** Fecha local de dentro de `dias` días (`AAAA-MM-DD`). */
function fechaMasDias(dias: number): string {
  const fecha = new Date();
  fecha.setDate(fecha.getDate() + dias);
  const desplazamiento = fecha.getTimezoneOffset() * 60_000;

  return new Date(fecha.getTime() - desplazamiento).toISOString().slice(0, 10);
}

export function ComprobanteCrearPage() {
  useDocumentTitle('Gestión · Generar comprobante');

  const navegar = useNavigate();

  const [tipo, setTipo] = useState<'FACTURA' | 'BOLETA'>('FACTURA');
  const [serie, setSerie] = useState('');
  const [clienteId, setClienteId] = useState(0);
  const [direccion, setDireccion] = useState('');
  const [fechaEmision, setFechaEmision] = useState(() => hoyIso());
  const [fechaVencimiento, setFechaVencimiento] = useState(() => hoyIso());
  const [condicionPago, setCondicionPago] = useState<'CONTADO' | 'CONTADO ADELANTADO' | 'CREDITO' | 'CREDITO CON CUOTAS'>('CONTADO');
  const [exonerado, setExonerado] = useState<'Y' | 'N'>('N');
  const [observaciones, setObservaciones] = useState('');
  const [guiaRemitente, setGuiaRemitente] = useState('');
  const [guiaTransportista, setGuiaTransportista] = useState('');
  const [lineas, setLineas] = useState<LineaCrearComprobante[]>([]);

  // Detracción.
  const [conDetraccion, setConDetraccion] = useState(false);
  const [tipoDetraccion, setTipoDetraccion] = useState('');
  const [porcentajeDetraccion, setPorcentajeDetraccion] = useState('');
  const [cuentaDetraccion, setCuentaDetraccion] = useState('');
  const [metodoDetraccion, setMetodoDetraccion] = useState('');

  // Pago contado.
  const [destino, setDestino] = useState<'caja_chica' | 'transferencia'>('caja_chica');
  const [referenciaContado, setReferenciaContado] = useState('');
  const [bancoId, setBancoId] = useState(0);
  const [referenciaBancaria, setReferenciaBancaria] = useState('');

  // Crédito y cuotas.
  const [creditoFecha, setCreditoFecha] = useState(() => fechaMasDias(30));
  const [cuotas, setCuotas] = useState<{ fecha: string; monto: string }[]>([{ fecha: '', monto: '' }]);

  const [agregar, setAgregar] = useState(false);
  const [confirmar, setConfirmar] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const catalogo = useCatalogosCrear(tipo);
  const crear = useCrearComprobante();

  // Clave del envío: se mantiene si hay que reintentar y se renueva al confirmar, para que un
  // doble clic no genere dos comprobantes (ni dos pagos, ni dos envíos a SUNAT).
  const claveDelEnvio = useRef(nuevaClaveDeEnvio());

  const empresa = catalogo.data?.empresa;
  const clientes = catalogo.data?.clientes ?? [];
  const productos = catalogo.data?.productos ?? [];
  const series = catalogo.data?.series ?? [];
  const bancos = catalogo.data?.bancos ?? [];
  const tiposDetraccion = catalogo.data?.tipos_detraccion ?? [];
  const metodosDetraccion = catalogo.data?.metodos_pago_detraccion ?? [];

  const cliente = clientes.find((item) => item.id === clienteId);

  // Al cambiar el tipo se limpia la serie y al elegir cliente su dirección.
  const cambiarTipo = (valor: 'FACTURA' | 'BOLETA') => {
    setTipo(valor);
    setSerie('');
  };

  const elegirCliente = (id: number) => {
    setClienteId(id);
    const item = clientes.find((c) => c.id === id);
    setDireccion(item?.direccion ?? '');
  };

  // Totales.
  const totales = useMemo(() => {
    const total = dinero(lineas.reduce((suma, linea) => suma + linea.total, 0));
    const exoneradoActual = exonerado === 'Y';
    const operacionTotal = exoneradoActual ? total : dinero(total / IGV);
    const igv = exoneradoActual ? 0 : dinero(total - operacionTotal);
    const porcentaje = Number.parseFloat(porcentajeDetraccion) || 0;
    const detraccion = conDetraccion ? dinero(total * (porcentaje / 100)) : 0;
    const totalPagar = dinero(total - detraccion);

    return { total, operacionTotal, igv, detraccion, totalPagar };
  }, [lineas, exonerado, porcentajeDetraccion, conDetraccion]);

  const retencionCliente = cliente?.retencion ? cliente.retencion_porcentaje : 0;

  // Condiciones del formulario (el backend vuelve a validarlas).
  const detraccionCompleta =
    !conDetraccion ||
    (tipoDetraccion !== '' &&
      metodoDetraccion !== '' &&
      cuentaDetraccion !== '' &&
      (Number.parseFloat(porcentajeDetraccion) || 0) > 0);

  const sumaCuotas = cuotas.reduce((suma, cuota) => suma + (Number.parseFloat(cuota.monto) || 0), 0);
  const cuotasCompletas =
    condicionPago !== 'CREDITO CON CUOTAS' ||
    (cuotas.some((cuota) => cuota.fecha !== '') && Math.abs(sumaCuotas - totales.totalPagar) < 0.02);

  const creditoCompleto = condicionPago !== 'CREDITO' || creditoFecha !== '';
  const transferenciaCompleta = destino !== 'transferencia' || bancoId > 0;

  const puedeRegistrar =
    clienteId > 0 &&
    serie !== '' &&
    lineas.length > 0 &&
    fechaEmision !== '' &&
    !crear.isPending &&
    (tipo !== 'FACTURA' || String(cliente?.tipo_doc) === '6') &&
    detraccionCompleta &&
    cuotasCompletas &&
    creditoCompleto &&
    transferenciaCompleta;

  const registrar = () => {
    setConfirmar(false);
    setError(null);

    crear.mutate(
      {
        tipo_comprobante: tipo,
        serie,
        cliente_id: clienteId,
        cliente_direccion: direccion,
        fecha_emision: fechaEmision,
        fecha_vencimiento:
          condicionPago === 'CREDITO'
            ? creditoFecha
            : condicionPago === 'CONTADO' || condicionPago === 'CONTADO ADELANTADO'
              ? fechaEmision
              : fechaVencimiento,
        condicion_pago: condicionPago,
        exonerado,
        lineas,
        observaciones,
        guia_remision_remitente: guiaRemitente,
        guia_remision_transportista: guiaTransportista,
        detraccion: conDetraccion
          ? {
              tipo_detraccion: tipoDetraccion,
              porcentaje: Number.parseFloat(porcentajeDetraccion) || 0,
              cuenta_bancaria: cuentaDetraccion,
              metodo_pago: metodoDetraccion,
            }
          : undefined,
        contado:
          condicionPago === 'CONTADO' || condicionPago === 'CONTADO ADELANTADO'
            ? {
                destino,
                referencia: referenciaContado,
                banco_id: bancoId,
                referencia_bancaria: referenciaBancaria,
              }
            : undefined,
        credito:
          condicionPago === 'CREDITO'
            ? { metodo_pago: 'Factura a 30 días', fecha: creditoFecha, monto: totales.totalPagar }
            : undefined,
        cuotas:
          condicionPago === 'CREDITO CON CUOTAS'
            ? cuotas
                .filter((cuota) => cuota.fecha)
                .map((cuota) => ({ fecha: cuota.fecha, monto: Number.parseFloat(cuota.monto) || 0 }))
            : undefined,
        clave_idempotencia: claveDelEnvio.current,
      },
      {
        onSuccess: (respuesta) => {
          claveDelEnvio.current = nuevaClaveDeEnvio();
          setAviso(respuesta.mensaje);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        },
        onError: (fallo) => {
          setError(fallo instanceof ApiError ? fallo.message : 'No se pudo crear el comprobante.');
          window.scrollTo({ top: 0, behavior: 'smooth' });
        },
      },
    );
  };

  return (
    <div className="space-y-5">
      <header>
        <Link
          to="/gestion/administracion/comprobantes"
          className="mb-2 inline-flex items-center gap-1.5 text-xs font-semibold text-ink-500 hover:text-brand-700"
        >
          <ArrowLeft className="size-3.5" aria-hidden="true" />
          Volver a comprobantes
        </Link>
        <h1 className="text-2xl font-bold text-ink-900">Generar comprobante electrónico</h1>
        <p className="mt-1 text-sm text-ink-500">
          Crea una factura o boleta administrativa y la deja en la cola de SUNAT.
        </p>
      </header>

      <AdministracionNav active="comprobantes" />

      {aviso ? (
        <Alert variant="success" title="Listo">
          {aviso}
        </Alert>
      ) : null}

      {error ? (
        <Alert variant="error" title="No se pudo completar la acción">
          {error}
        </Alert>
      ) : null}

      {catalogo.isLoading ? <Skeleton className="h-72 w-full" /> : null}

      {catalogo.data ? (
        <>
          {/* Empresa y fechas */}
          <div className="rounded-card border border-ink-100 p-4">
            <p className="text-sm font-semibold text-ink-800">
              {empresa?.razon_social} - {empresa?.ruc}
            </p>
            <p className="text-xs text-ink-500">{empresa?.direccion}</p>

            <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Input
                label="Fecha emisión"
                type="date"
                value={fechaEmision}
                min={hoyMenosDias(empresa?.dias_boleta_masiva ?? 3)}
                max={hoyIso()}
                onChange={(evento) => setFechaEmision(evento.target.value)}
              />
              <Input
                label="Fecha vencimiento"
                type="date"
                value={fechaVencimiento}
                min={hoyIso()}
                onChange={(evento) => setFechaVencimiento(evento.target.value)}
              />
              <label className="flex flex-col gap-1.5 text-sm">
                <span className="font-medium text-ink-700">Tipo comprobante</span>
                <Select
                  className={CONTROL}
                  value={tipo}
                  onChange={(evento) => cambiarTipo(evento.target.value as 'FACTURA' | 'BOLETA')}
                >
                  <option value="FACTURA">FACTURA ELECTRÓNICA</option>
                  <option value="BOLETA">BOLETA DE VENTA ELECTRÓNICA</option>
                </Select>
              </label>
              <label className="flex flex-col gap-1.5 text-sm">
                <span className="font-medium text-ink-700">Serie</span>
                <Select
                  className={CONTROL}
                  value={serie}
                  onChange={(evento) => setSerie(evento.target.value)}
                >
                  <option value="">Seleccione</option>
                  {series.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </Select>
              </label>
              <Input label="Tipo operación" value={conDetraccion ? 'DETRACCION' : 'VENTA INTERNA'} readOnly />
              <Input label="Moneda" value="SOLES" readOnly />
              <Input
                label="Tipo de cambio"
                type="number"
                step="0.001"
                defaultValue="3.728"
                disabled
              />
              <div className="flex items-end">
                <Button
                  variant={conDetraccion ? 'secondary' : 'outline'}
                  className="w-full"
                  onClick={() => {
                    setConDetraccion((previo) => !previo);
                    if (!conDetraccion) setCuentaDetraccion(empresa?.cuenta_bancaria_detraccion ?? '');
                  }}
                >
                  {conDetraccion ? 'Quitar detracción' : 'Operación sujeta a detracción'}
                </Button>
              </div>
            </div>

            {/* Detracción */}
            {conDetraccion ? (
              <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <label className="flex flex-col gap-1.5 text-sm">
                  <span className="font-medium text-ink-700">Tipo detracción</span>
                  <Select
                    className={CONTROL}
                    value={tipoDetraccion}
                    onChange={(evento) => {
                      setTipoDetraccion(evento.target.value);
                      const tipoDet = tiposDetraccion.find((t) => t.codigo === evento.target.value);
                      if (tipoDet) setPorcentajeDetraccion(String(tipoDet.porcentaje));
                    }}
                  >
                    <option value="">Seleccione</option>
                    {tiposDetraccion.map((t) => (
                      <option key={t.codigo} value={t.codigo}>
                        {t.codigo} · {t.descripcion}
                      </option>
                    ))}
                  </Select>
                </label>
                <Input
                  label="Porcentaje (%)"
                  type="number"
                  min="0"
                  step="0.01"
                  value={porcentajeDetraccion}
                  onChange={(evento) => setPorcentajeDetraccion(evento.target.value)}
                />
                <Input
                  label="Cuenta bancaria"
                  value={cuentaDetraccion}
                  onChange={(evento) => setCuentaDetraccion(evento.target.value)}
                />
                <label className="flex flex-col gap-1.5 text-sm">
                  <span className="font-medium text-ink-700">Método de pago</span>
                  <Select
                    className={CONTROL}
                    value={metodoDetraccion}
                    onChange={(evento) => setMetodoDetraccion(evento.target.value)}
                  >
                    <option value="">Seleccione</option>
                    {metodosDetraccion.map((m) => (
                      <option key={m.codigo} value={m.codigo}>
                        {m.codigo} · {m.descripcion}
                      </option>
                    ))}
                  </Select>
                </label>
              </div>
            ) : null}
          </div>

          {/* Cliente */}
          <div className="rounded-card border border-ink-100 p-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="flex flex-col gap-1.5 text-sm">
                <span className="font-medium text-ink-700">Cliente</span>
                <Select
                  className={CONTROL}
                  value={clienteId}
                  onChange={(evento) => elegirCliente(Number(evento.target.value))}
                  searchable
                >
                  <option value={0}>SELECCIONE</option>
                  {clientes.map((item) => (
                    <option key={item.id} value={item.id} data-search={item.numero_doc}>
                      {item.numero_doc} ({item.tipo_doc === '6' ? 'RUC' : item.tipo_doc === '1' ? 'DNI' : item.tipo_doc}) - {item.nombre}
                    </option>
                  ))}
                </Select>
              </label>
              <Input
                label="Dirección"
                value={direccion}
                onChange={(evento) => setDireccion(evento.target.value)}
              />
            </div>

            {cliente ? (
              <p className="mt-2 text-xs text-ink-500">
                {cliente.tipo_doc === '6' ? 'RUC' : 'DNI'} {cliente.numero_doc}
                {cliente.retencion ? ` · con retención del ${cliente.retencion_porcentaje}%` : ' · sin retención'}
              </p>
            ) : null}

            {tipo === 'FACTURA' && cliente && String(cliente.tipo_doc) !== '6' ? (
              <p className="mt-1 text-xs font-medium text-rose-600">
                Para emitir FACTURA se requiere un cliente con RUC.
              </p>
            ) : null}
          </div>

          {/* Información adicional */}
          <div className="rounded-card border border-ink-100 p-4">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <Input
                label="Guía de remisión remitente"
                value={guiaRemitente}
                onChange={(evento) => setGuiaRemitente(evento.target.value)}
              />
              <Input
                label="Guía de remisión transportista"
                value={guiaTransportista}
                onChange={(evento) => setGuiaTransportista(evento.target.value)}
              />
              <Input
                label="Observaciones"
                value={observaciones}
                onChange={(evento) => setObservaciones(evento.target.value)}
              />
            </div>

            {empresa?.ley_27037 ? (
              <div className="mt-3">
                <label className="flex flex-col gap-1.5 text-sm">
                  <span className="font-medium text-ink-700">IGV (Ley 27037)</span>
                  <Select
                    className={CONTROL}
                    value={exonerado}
                    onChange={(evento) => setExonerado(evento.target.value as 'Y' | 'N')}
                  >
                    <option value="N">Desglose de IGV</option>
                    <option value="Y">Exonerado</option>
                  </Select>
                </label>
              </div>
            ) : null}
          </div>

          {/* Productos */}
          <div className="rounded-card border border-ink-100 p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <h2 className="text-sm font-semibold text-ink-800">Detalle de la venta</h2>
              <Button size="sm" leftIcon={<Plus className="size-4" aria-hidden="true" />} onClick={() => setAgregar(true)}>
                Agregar producto
              </Button>
            </div>

            {lineas.length ? (
              <div className="overflow-auto rounded-card border border-ink-100">
                <table className="w-full border-collapse text-[13px]">
                  <thead className="bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                    <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                      <th className="px-3 py-2">Descripción</th>
                      <th className="px-3 py-2">Unidad</th>
                      <th className="px-3 py-2 text-right">Cantidad</th>
                      <th className="px-3 py-2 text-right">P. unitario</th>
                      <th className="px-3 py-2 text-right">Subtotal</th>
                      <th className="px-3 py-2 text-right">IGV</th>
                      <th className="px-3 py-2 text-right">Total</th>
                      <th className="px-3 py-2">Placas</th>
                      <th className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {lineas.map((linea, indice) => {
                      const subtotal = exonerado === 'Y' ? linea.total : dinero(linea.total / IGV);
                      const igv = exonerado === 'Y' ? 0 : dinero(linea.total - subtotal);

                      return (
                        <tr key={`${linea.producto_id}-${indice}`} className="border-t border-ink-100">
                          <td className="px-3 py-2">{linea.descripcion_pdf || `Producto #${linea.producto_id}`}</td>
                          <td className="px-3 py-2">{linea.unidad ?? 'GLL'}</td>
                          <td className="px-3 py-2 text-right">{linea.cantidad.toFixed(3)}</td>
                          <td className="px-3 py-2 text-right">{monto(linea.precio)}</td>
                          <td className="px-3 py-2 text-right">{monto(subtotal)}</td>
                          <td className="px-3 py-2 text-right">{monto(igv)}</td>
                          <td className="px-3 py-2 text-right">{monto(linea.total)}</td>
                          <td className="px-3 py-2">{linea.placas.join(', ') || '—'}</td>
                          <td className="px-3 py-2 text-right">
                            <button
                              type="button"
                              className="rounded p-1 text-rose-500 hover:bg-rose-50"
                              aria-label="Eliminar línea"
                              onClick={() => setLineas((previas) => previas.filter((_, i) => i !== indice))}
                            >
                              <Trash2 className="size-4" aria-hidden="true" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-ink-500">Todavía no hay productos. Agrega la primera línea.</p>
            )}

            {/* Totales */}
            <div className="mt-3 ml-auto max-w-xs space-y-1 text-sm">
              <div className="flex justify-between text-ink-600">
                <span>Op. total</span>
                <span>{monto(totales.operacionTotal)}</span>
              </div>
              <div className="flex justify-between text-ink-600">
                <span>IGV</span>
                <span>{monto(totales.igv)}</span>
              </div>
              <div className="flex justify-between font-semibold text-ink-900">
                <span>Total</span>
                <span>{monto(totales.total)}</span>
              </div>
              {conDetraccion ? (
                <div className="flex justify-between text-ink-600">
                  <span>Detracción</span>
                  <span>{monto(totales.detraccion)}</span>
                </div>
              ) : null}
              {conDetraccion ? (
                <div className="flex justify-between font-semibold text-ink-900">
                  <span>Total a pagar</span>
                  <span>{monto(totales.totalPagar)}</span>
                </div>
              ) : null}
              {retencionCliente > 0 ? (
                <p className="text-xs text-ink-500">
                  Retención del cliente: {retencionCliente}% sobre {monto(totales.total)} ={' '}
                  {monto(dinero(totales.total * (retencionCliente / 100)))}.
                </p>
              ) : null}
            </div>
          </div>

          {/* Condición de pago */}
          <div className="rounded-card border border-ink-100 p-4">
            <label className="flex flex-col gap-1.5 text-sm sm:max-w-xs">
              <span className="font-medium text-ink-700">Condición de pago</span>
              <Select
                className={CONTROL}
                value={condicionPago}
                onChange={(evento) => setCondicionPago(evento.target.value as typeof condicionPago)}
              >
                <option value="CONTADO">Contado</option>
                <option value="CONTADO ADELANTADO">Pago por Adelantado</option>
                <option value="CREDITO CON CUOTAS">Crédito con cuotas</option>
                <option value="CREDITO">Crédito</option>
              </Select>
            </label>

            {condicionPago === 'CONTADO' || condicionPago === 'CONTADO ADELANTADO' ? (
              <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <label className="flex flex-col gap-1.5 text-sm">
                  <span className="font-medium text-ink-700">Destino del pago</span>
                  <Select
                    className={CONTROL}
                    value={destino}
                    onChange={(evento) => setDestino(evento.target.value as typeof destino)}
                  >
                    <option value="caja_chica">Caja Chica</option>
                    <option value="transferencia">Transferencia bancaria</option>
                  </Select>
                </label>
                {destino === 'caja_chica' ? (
                  <Input
                    label="Concepto / Referencia"
                    value={referenciaContado}
                    onChange={(evento) => setReferenciaContado(evento.target.value)}
                  />
                ) : (
                  <>
                    <label className="flex flex-col gap-1.5 text-sm">
                      <span className="font-medium text-ink-700">Cuenta destino</span>
                      <Select
                        className={CONTROL}
                        value={bancoId}
                        onChange={(evento) => setBancoId(Number(evento.target.value))}
                      >
                        <option value={0}>SELECCIONE</option>
                        {bancos.map((banco) => (
                          <option key={banco.id} value={banco.id}>
                            {banco.nombre} - {banco.numero ?? ''}
                          </option>
                        ))}
                      </Select>
                    </label>
                    <Input
                      label="Referencia (Nro. operación)"
                      value={referenciaBancaria}
                      onChange={(evento) => setReferenciaBancaria(evento.target.value)}
                    />
                  </>
                )}
                <Input label="Monto" value={monto(totales.totalPagar)} readOnly />
              </div>
            ) : null}

            {condicionPago === 'CREDITO' ? (
              <div className="mt-3 grid gap-3 sm:grid-cols-3">
                <Input label="Método de pago" value="Factura a 30 días" readOnly />
                <Input
                  label="Fecha"
                  type="date"
                  min={hoyIso()}
                  value={creditoFecha}
                  onChange={(evento) => setCreditoFecha(evento.target.value)}
                />
                <Input label="Monto" value={monto(totales.totalPagar)} readOnly />
              </div>
            ) : null}

            {condicionPago === 'CREDITO CON CUOTAS' ? (
              <div className="mt-3 space-y-2">
                {cuotas.map((cuota, indice) => (
                  <div key={indice} className="grid grid-cols-[1fr_1fr_auto] gap-2 sm:max-w-md">
                    <Input
                      label={indice === 0 ? 'Fecha cuota' : undefined}
                      type="date"
                      value={cuota.fecha}
                      onChange={(evento) =>
                        setCuotas((previas) =>
                          previas.map((item, i) => (i === indice ? { ...item, fecha: evento.target.value } : item)),
                        )
                      }
                    />
                    <Input
                      label={indice === 0 ? 'Monto' : undefined}
                      type="number"
                      min="0"
                      step="0.01"
                      value={cuota.monto}
                      onChange={(evento) =>
                        setCuotas((previas) =>
                          previas.map((item, i) => (i === indice ? { ...item, monto: evento.target.value } : item)),
                        )
                      }
                    />
                    <div className="flex items-end pb-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label="Quitar cuota"
                        disabled={cuotas.length === 1}
                        onClick={() => setCuotas((previas) => previas.filter((_, i) => i !== indice))}
                      >
                        <Trash2 className="size-4" aria-hidden="true" />
                      </Button>
                    </div>
                  </div>
                ))}
                <Button variant="ghost" size="sm" leftIcon={<Plus className="size-4" aria-hidden="true" />} onClick={() => setCuotas((previas) => [...previas, { fecha: '', monto: '' }])}>
                  Agregar cuota
                </Button>

                {cuotas.some((cuota) => cuota.fecha !== '') && Math.abs(sumaCuotas - totales.totalPagar) >= 0.02 ? (
                  <p className="text-xs font-medium text-rose-600">
                    La suma de las cuotas ({monto(sumaCuotas)}) debe ser igual al total a pagar ({monto(totales.totalPagar)}).
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="flex flex-wrap gap-3">
            <Button
              leftIcon={<Check className="size-4" aria-hidden="true" />}
              loading={crear.isPending}
              disabled={!puedeRegistrar}
              onClick={() => setConfirmar(true)}
            >
              Registrar
            </Button>
            <Button variant="ghost" onClick={() => navegar('/gestion/administracion/comprobantes')}>
              Cancelar
            </Button>
          </div>
        </>
      ) : null}

      <AgregarProductoDialog
        open={agregar}
        productos={productos}
        onClose={() => setAgregar(false)}
        onAgregar={(linea) => {
          setLineas((previas) => [...previas, linea]);
          setAgregar(false);
        }}
      />

      <ConfirmDialog
        open={confirmar}
        title="Generar comprobante"
        description={`Se registrará una ${tipo} ${serie} por S/ ${monto(totales.total)} con ${lineas.length} línea(s).`}
        confirmLabel="Sí, generar"
        tone="danger"
        loading={crear.isPending}
        onConfirm={registrar}
        onClose={() => setConfirmar(false)}
      />
    </div>
  );
}

/** Diálogo para agregar una línea al comprobante. */
function AgregarProductoDialog({
  open,
  productos,
  onClose,
  onAgregar,
}: {
  open: boolean;
  productos: { id: number; nombre: string; precio: number; unidad_medida: string | null }[];
  onClose: () => void;
  onAgregar: (linea: LineaCrearComprobante) => void;
}) {
  const [productoId, setProductoId] = useState(0);
  const [descripcion, setDescripcion] = useState('');
  const [precio, setPrecio] = useState('');
  const [cantidad, setCantidad] = useState('');
  const [placa, setPlaca] = useState('');

  const producto = productos.find((p) => p.id === productoId);
  const precioNumero = Number.parseFloat(precio) || 0;
  const cantidadNumero = Number.parseFloat(cantidad) || 0;
  const total = dinero(precioNumero * cantidadNumero);

  const elegir = (id: number) => {
    setProductoId(id);
    const item = productos.find((p) => p.id === id);

    if (item) {
      setDescripcion(item.nombre);
      setPrecio(String(item.precio));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Agregar producto"
      size="sm"
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            size="sm"
            leftIcon={<FilePlus2 className="size-4" aria-hidden="true" />}
            disabled={!(productoId > 0 && precioNumero > 0 && cantidadNumero > 0)}
            onClick={() =>
              onAgregar({
                producto_id: productoId,
                descripcion_pdf: descripcion,
                unidad: producto?.unidad_medida ?? 'GLL',
                precio: precioNumero,
                cantidad: con3(cantidadNumero),
                total,
                placas: placa.split(',').map((p) => p.trim()).filter(Boolean),
              })
            }
          >
            Agregar
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <label className="flex flex-col gap-1.5 text-sm">
          <span className="font-medium text-ink-700">Producto</span>
          <Select
            className={CONTROL}
            value={productoId}
            onChange={(evento) => elegir(Number(evento.target.value))}
          >
            <option value={0}>Seleccionar</option>
            {productos.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nombre} ({p.unidad_medida ?? 'GLL'})
              </option>
            ))}
          </Select>
        </label>
        <Input label="Descripción en PDF" value={descripcion} onChange={(evento) => setDescripcion(evento.target.value)} />
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Precio / Uni (Inc. IGV)"
            type="number"
            min="0.01"
            step="0.01"
            value={precio}
            onChange={(evento) => setPrecio(evento.target.value)}
          />
          <Input
            label="Cantidad (GLN/LTR)"
            type="number"
            min="0.001"
            step="0.001"
            value={cantidad}
            onChange={(evento) => setCantidad(evento.target.value)}
          />
        </div>
        <div className="rounded-card border border-ink-100 bg-ink-50/50 p-3 text-sm">
          <div className="flex justify-between text-ink-600">
            <span>Subtotal</span>
            <span>{monto(dinero(total / IGV))}</span>
          </div>
          <div className="flex justify-between text-ink-600">
            <span>IGV (18%)</span>
            <span>{monto(dinero(total - total / IGV))}</span>
          </div>
          <div className="flex justify-between font-semibold text-ink-900">
            <span>Total</span>
            <span>{monto(total)}</span>
          </div>
        </div>
        <Input
          label="Placa(s)"
          placeholder="Ej: ABC-123, XYZ-456"
          value={placa}
          onChange={(evento) => setPlaca(evento.target.value)}
        />
      </div>
    </Dialog>
  );
}
